//inpired by Kern Handa https://kernhanda.github.io/tutorial-typescript-canvas-drawing/

import { DrawingAnalizer } from './drawingAnalizer';

export class DrawingPath {
  public clickX: number[] = [];
  public clickY: number[] = [];
  public clickDrag: boolean[] = [];
  private points: { x: number; y: number; isDragging: boolean }[] = [];

  public addPoint(x: number, y: number, isDragging: boolean) {
    this.clickX.push(x);
    this.clickY.push(y);
    this.clickDrag.push(isDragging);
    this.points.push({ x, y, isDragging });
  }

  public clear() {
    this.clickX = [];
    this.clickY = [];
    this.clickDrag = [];
    this.points = [];
  }

  public countSegments(minDistance = 5): number {
    let segmentCount = 0;
    for (let i = 1; i < this.points.length; i++) {
      const { x: x1, y: y1 } = this.points[i - 1];
      const { x: x2, y: y2 } = this.points[i];
      const distance = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

      if (distance > minDistance && this.points[i].isDragging) {
        segmentCount++;
      }
    }
    return segmentCount;
  }

  public getTotalLength(): number {
    let totalLength = 0;
    for (let i = 1; i < this.points.length; i++) {
      const { x: x1, y: y1 } = this.points[i - 1];
      const { x: x2, y: y2 } = this.points[i];
      totalLength += Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    }
    return totalLength;
  }
}

export class Drawing {
  protected canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D | null;
  private paint = false;
  protected drawingPath: DrawingPath;

  constructor(id: string, value?: string) {
    this.canvas = document.getElementById(id) as HTMLCanvasElement;
    this.context = this.canvas.getContext('2d');
    const context = this.context;
    if (context && value) {
      const img = new Image();
      img.onload = function () {
        context.drawImage(img, 0, 0); // offset
      };
      img.src = value;
    }

    this.drawingPath = new DrawingPath();

    this.clearCanvas();
    this.drawingPath.clear();
    this.createUserEvents();
    this.resizeCanvas(id);
  }

  private resizeCanvas(id: string) {
    const canvas = document.getElementById(id) as HTMLCanvasElement;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const resizeMe = () => {
      // Ustawienie rzeczywistej wielkości kanwy
      canvas.width = canvas.clientWidth; // Ustawienie rzeczywistej szerokości na podstawie obliczonego CSS width
      canvas.height = canvas.clientHeight; // Ustawienie rzeczywistej wysokości na podstawie obliczonego CSS height

      that.setContextCanvas();
      that.redraw();
    };

    resizeMe();
    window.addEventListener('resize', resizeMe); // Dostosowanie na każdą zmianę rozmiaru okna
  }

  private setContextCanvas() {
    if (this.context) {
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';
      this.context.lineWidth = 1;
      this.context.strokeStyle = 'hsl(208, 100%, 43%)';
      ///this.context.filter = 'blur(0.5px)'; - to spowalnia rysowanie w chrome
    }
  }

  private createUserEvents() {
    const canvas = this.canvas;

    canvas.addEventListener('mousedown', this.pressEventHandler);
    canvas.addEventListener('mousemove', this.dragEventHandler);
    canvas.addEventListener('mouseup', this.releaseEventHandler);
    canvas.addEventListener('mouseout', this.cancelEventHandler);

    canvas.addEventListener('touchstart', this.pressEventHandler);
    canvas.addEventListener('touchmove', this.dragEventHandler);
    canvas.addEventListener('touchend', this.releaseEventHandler);
    canvas.addEventListener('touchcancel', this.cancelEventHandler);
    canvas.addEventListener('clear', this.clearEventHandler);
  }

  private redraw() {
    const clickX = this.drawingPath.clickX;
    const clickY = this.drawingPath.clickY;
    const clickDrag = this.drawingPath.clickDrag;

    const context = this.context;

    if (context) {
      for (let i = 0; i < clickX.length; ++i) {
        context.beginPath();
        if (clickDrag[i] && i) {
          context.moveTo(clickX[i - 1], clickY[i - 1]);
        } else {
          context.moveTo(clickX[i] - 1, clickY[i]);
        }
        context.lineTo(clickX[i], clickY[i]);
        context.stroke();
      }
      context.closePath();
    }
  }

  private clearCanvas() {
    this.context &&
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.drawingPath.clear();
  }

  private clearEventHandler = () => {
    this.clearCanvas();
  };

  private releaseEventHandler = () => {
    this.paint = false;
    this.redraw();
  };

  private cancelEventHandler = () => {
    this.paint = false;
  };
  private pressEventHandler = (e: MouseEvent | TouchEvent) => {
    let mouseX = (e as TouchEvent).changedTouches
      ? (e as TouchEvent).changedTouches[0].pageX
      : (e as MouseEvent).pageX;
    let mouseY = (e as TouchEvent).changedTouches
      ? (e as TouchEvent).changedTouches[0].pageY
      : (e as MouseEvent).pageY;
    mouseX -= this.canvas.offsetLeft;
    mouseY -= this.canvas.offsetTop;

    this.paint = true;
    this.drawingPath.addPoint(mouseX, mouseY, false);
    this.redraw();
  };

  private dragEventHandler = (e: MouseEvent | TouchEvent) => {
    let mouseX = (e as TouchEvent).changedTouches
      ? (e as TouchEvent).changedTouches[0].pageX
      : (e as MouseEvent).pageX;
    let mouseY = (e as TouchEvent).changedTouches
      ? (e as TouchEvent).changedTouches[0].pageY
      : (e as MouseEvent).pageY;
    mouseX -= this.canvas.offsetLeft;
    mouseY -= this.canvas.offsetTop;

    if (this.paint) {
      this.drawingPath.addPoint(mouseX, mouseY, true);
      this.redraw();
    }

    e.preventDefault();
  };

  public getAsDataUrl() {
    return this.canvas.toDataURL();
  }

  public static getDrawingAsDataUrl(
    canvas: HTMLCanvasElement,
    paramCheckComplex?: number[],
  ) {
    //const canvas = document.getElementById(id) as HTMLCanvasElement;
    return DrawingAnalizer.isNotSufficientlyComplex(
      canvas,
      paramCheckComplex !== undefined,
      paramCheckComplex,
    ) >= 0
      ? undefined
      : canvas.toDataURL();
  }

  public static getProblemDrawingCode(
    canvas: HTMLCanvasElement,
    paramCheckComplex?: number[],
  ) {
    //const canvas = document.getElementById(id) as HTMLCanvasElement;
    return DrawingAnalizer.isNotSufficientlyComplex(
      canvas,
      paramCheckComplex !== undefined,
      paramCheckComplex,
    );
  }

  public static clear(id: string) {
    const event = new Event('clear');
    document.getElementById(id)?.dispatchEvent(event);
  }
}
